import { useLazyQuery } from "@apollo/client";
import equal from "deep-equal";
import { ComponentProps, useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import CircleButton from "@components/buttons/CircleButton";
import DeleteConfirmationAlert from "@components/DeleteConfirmationAlert";
import JourneyTagFilters from "@components/forms/JourneyTagFilters";
import JourneyTagsMergeForm from "@components/forms/JourneyTagsMergeForm";
import HandleQuery from "@components/HandleQuery";
import Tag from "@components/labels/Tag";
import Modal from "@components/Modal";
import Table, { TableHeader } from "@components/Table";
import InternalLink from "@components/typography/InternalLink";
import useSubscribeToAllJourneyTags from "@hooks/subscriptions/useSubscribeToAllJourneyTags";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyAdmin from "@hooks/useMyAdmin";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import useOpener from "@hooks/useOpener";
import { TimePeriodEnum } from "@typing/Enums";
import {
  AdminGlobalRoleEnum,
  GetJourneyTagsDocument,
  GetJourneyTagsQuery,
  GetJourneyTagsQueryVariables,
  JourneyTagDestroyDocument,
  JourneyTagDestroyMutation,
  JourneyTagDestroyMutationVariables,
  JourneyTagSortByEnum,
  SortDirectionEnum
} from "@typing/Generated";
import { calculateDatesFromTimePeriod } from "@utils/modelUtils";

type Filters = ComponentProps<typeof JourneyTagFilters>["filters"];

type Props = {
  expertId?: string;
};

type Tag = NonNullable<NonNullable<NonNullable<GetJourneyTagsQuery["journeyTags"]["edges"]>[number]>["node"]>;

const JourneyTagsTable = ({ expertId }: Props) => {
  const intl = useIntl();

  const myAdmin = useMyAdmin();

  const deleteOpener = useOpener();
  const mergeOpener = useOpener();

  const [deletingTag, setDeletingTag] = useState<Tag | null>(null);
  const [mergingTag, setMergingTag] = useState<Tag | null>(null);

  const [getJourneyTags, getJourneyTagsQuery] = useLazyQuery<GetJourneyTagsQuery, GetJourneyTagsQueryVariables>(
    GetJourneyTagsDocument
  );

  useSubscribeToAllJourneyTags(() => {
    getJourneyTagsQuery.refetch();
  });

  const [filters, setFilters] = useState<Filters>({
    employerId: null,
    searchTerm: "",
    selfPaid: false,
    timePeriod: TimePeriodEnum.ALL_TIME
  });

  const handleFilterChange = useCallback(
    (newFilters: Filters) => {
      if (!equal(newFilters, filters)) {
        setFilters(newFilters);
      }
    },
    [filters]
  );

  const canManage = useMemo(() => {
    const allowedAdminRoles = [AdminGlobalRoleEnum.EMPLOYER_MANAGER, AdminGlobalRoleEnum.SUPER_USER];
    return !expertId && (myAdmin?.adminGlobalRoles ?? []).some(role => allowedAdminRoles.includes(role));
  }, [expertId, myAdmin?.adminGlobalRoles]);

  const [sortBy, setSortBy] = useState<JourneyTagSortByEnum>(JourneyTagSortByEnum.TAG);
  const [sortDirection, setSortDirection] = useState<SortDirectionEnum>(SortDirectionEnum.ASCENDING);

  const journeyTags = useNodesFromConnection(getJourneyTagsQuery.data?.journeyTags);

  const tableHeaders: TableHeader<JourneyTagSortByEnum>[] = [
    { columnSortBy: JourneyTagSortByEnum.TAG },
    { columnSortBy: JourneyTagSortByEnum.ACTIVE_COUNT },
    { columnSortBy: JourneyTagSortByEnum.ARCHIVED_COUNT },
    { columnSortBy: JourneyTagSortByEnum.TOTAL_COUNT }
  ];

  if (canManage) {
    tableHeaders.push({ messageKey: "dictionary.actions" });
  }

  useEffect(() => {
    if (filters.timePeriod === TimePeriodEnum.CUSTOM && (!filters.startDate || !filters.endDate)) {
      return;
    }
    const dates = calculateDatesFromTimePeriod(filters.timePeriod, filters.startDate, filters.endDate, intl);

    getJourneyTags({
      variables: {
        employerId: filters.employerId,
        endDate: dates.endDate,
        expertId,
        searchTerm: filters.searchTerm,
        selfPaid: filters.selfPaid,
        sortBy,
        sortDirection,
        startDate: dates.startDate
      }
    });
  }, [expertId, filters, getJourneyTags, intl, sortBy, sortDirection]);

  const generateDeleteStartHandler = useCallback(
    (tag: Tag) => () => {
      setDeletingTag(tag);
      deleteOpener.open();
    },
    [deleteOpener]
  );

  const generateMergeStartHandler = useCallback(
    (tag: Tag) => () => {
      setMergingTag(tag);
      mergeOpener.open();
    },
    [mergeOpener]
  );

  const [destroyJourneyTag] = useMutationWithValidation<JourneyTagDestroyMutation, JourneyTagDestroyMutationVariables>(
    JourneyTagDestroyDocument,
    "journeyTag",
    {
      update: () => {
        getJourneyTagsQuery.refetch();
      }
    }
  );

  const handleDelete = useCallback(() => {
    if (deletingTag) {
      destroyJourneyTag({ id: deletingTag.id }).then(() => setDeletingTag(null));
    }
  }, [deletingTag, destroyJourneyTag]);

  return (
    <>
      <JourneyTagFilters filters={filters} onChange={handleFilterChange} />
      <HandleQuery query={getJourneyTagsQuery}>
        {data => (
          <Table<JourneyTagSortByEnum>
            fetchMore={getJourneyTagsQuery.fetchMore}
            headerKey="pages.admin.journeyTags.tableHeadings"
            headers={tableHeaders}
            pageInfo={data?.journeyTags.pageInfo}
            setSortBy={setSortBy}
            setSortDirection={setSortDirection}
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            {journeyTags.map(journeyTag => (
              <tr key={journeyTag.id}>
                <td>
                  {myAdmin ? (
                    <InternalLink stylized={false} to={`/admin/tags/${journeyTag.tag}`}>
                      <Tag color="secondary-500" text={journeyTag.tag} />
                    </InternalLink>
                  ) : (
                    <InternalLink stylized={false} to={`/expert/tags/${journeyTag.tag}`}>
                      <Tag color="secondary-500" text={journeyTag.tag} />
                    </InternalLink>
                  )}
                </td>
                <td>{journeyTag.count}</td>
                <td>{journeyTag.archivedCount}</td>
                <td>{journeyTag.count + journeyTag.archivedCount}</td>
                {canManage && (
                  <td>
                    <CircleButton
                      ariaLabelKey="accessibility.ariaLabels.edit"
                      fill="clear"
                      icon="wrench"
                      onClick={generateMergeStartHandler(journeyTag)}
                    />
                    <br />
                    <CircleButton
                      ariaLabelKey="accessibility.ariaLabels.delete"
                      fill="clear"
                      icon="trash"
                      onClick={generateDeleteStartHandler(journeyTag)}
                    />
                  </td>
                )}
              </tr>
            ))}
          </Table>
        )}
      </HandleQuery>
      <Modal opener={mergeOpener} title={intl.formatMessage({ id: "pages.admin.journeyTags.mergeModalTitle" })}>
        {() => (
          <JourneyTagsMergeForm
            defaultTag={mergingTag?.tag}
            onCancel={mergeOpener.close}
            onError={mergeOpener.close}
            onSuccess={mergeOpener.close}
          />
        )}
      </Modal>
      <DeleteConfirmationAlert
        messageKey="pages.admin.journeyTags.reallyDelete"
        onDelete={handleDelete}
        opener={deleteOpener}
      />
    </>
  );
};

export default JourneyTagsTable;
